import { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { AppointmentStatus, AppointmentType, SearchParameters, UtmSource } from '../../constants'
import { Appointment, AppointmentFormData } from '../../types'
import { useAppointments } from '../useAppointments'
import { useFeatureFlags } from '../useFeatureFlags'
import { useRescheduleContext } from '../useRescheduleContext'
import { useSearchParameters } from '../useSearchParameters'
import { useReferrerContext } from '../useReferrerContext'

interface UseDoubleBookingContext {
  hasCompletedHearingAidEvaluation: boolean
  hasConfirmedHearingAidEvaluation: boolean
  isHearingAidEvaluationEnabled: boolean
  isLoading: boolean
  nextAppointmentType: AppointmentType
  previouslyCompletedAppointment?: Appointment
  previouslyConfirmedAppointment?: Appointment
}

const isCompleted = (appointment: Appointment) => appointment.status.status === AppointmentStatus.COMPLETED
const isConfirmed = (appointment: Appointment) => appointment.status.status === AppointmentStatus.CONFIRMED
const confirmedOrCompletedStatuses = new Set([AppointmentStatus.CONFIRMED, AppointmentStatus.COMPLETED])
const isCompletedOrConfirmed = (appointment: Appointment) => confirmedOrCompletedStatuses.has(appointment.status.status)

const useDoubleBookingContext = (): UseDoubleBookingContext => {
  const [isPreventDoubleBookingHearingAidEvaluationsFlagEnabled] = useFeatureFlags<boolean>([
    'PREVENT_DOUBLE_BOOKING_HEARING_AID_EVALUATIONS',
  ])

  const { [SearchParameters.OPPORTUNITY_GID]: searchParamOpportunitySalesforceId } = useSearchParameters()
  const [focusedAppointmentType, setFocusedAppointmentType] = useState<AppointmentType>(AppointmentType.HEARING_AID_EVALUATION)
  const [shouldPreventDoubleBookingHearingAidEvaluations, setShouldPreventDoubleBookingHearingAidEvaluations] = useState<boolean>(false)
  const formContext = useFormContext<AppointmentFormData>()
  const selectedAppointmentType = formContext?.watch('type')
  const referrerContext = useReferrerContext()
  const isReferredByFittingFlow = referrerContext?.source === UtmSource.FITTER_COCKPIT
  const rescheduleContext = useRescheduleContext()
  const isRescheduling = Boolean(rescheduleContext?.reason)

  const opportunityGids: string[] | undefined =
    typeof searchParamOpportunitySalesforceId === 'string' ? [searchParamOpportunitySalesforceId] : searchParamOpportunitySalesforceId

  const { data: completedOrConfirmedAppointments = [], isLoading: areAppointmentsLoading } = useAppointments(
    {
      opportunity_gid: opportunityGids,
      sort_by: 'start_date',
      sort: 'desc',
      status: Array.from(confirmedOrCompletedStatuses),
      type: [AppointmentType.HEARING_AID_EVALUATION],
    },
    Boolean(opportunityGids?.length && isPreventDoubleBookingHearingAidEvaluationsFlagEnabled)
  )

  useEffect(() => {
    if (selectedAppointmentType) {
      setFocusedAppointmentType(selectedAppointmentType)
    }

    const filteredAppointments = completedOrConfirmedAppointments.filter(isCompletedOrConfirmed)
    let prevent = false

    if (!areAppointmentsLoading && filteredAppointments.length && focusedAppointmentType === AppointmentType.HEARING_AID_EVALUATION) {
      prevent = true
    }

    setShouldPreventDoubleBookingHearingAidEvaluations(prevent)
  }, [
    areAppointmentsLoading,
    completedOrConfirmedAppointments,
    focusedAppointmentType,
    selectedAppointmentType,
    isPreventDoubleBookingHearingAidEvaluationsFlagEnabled,
  ])

  const completedAppointment = useMemo(() => completedOrConfirmedAppointments.find(isCompleted), [completedOrConfirmedAppointments])
  const confirmedAppointment = useMemo(() => completedOrConfirmedAppointments.find(isConfirmed), [completedOrConfirmedAppointments])
  const nextAppointmentType = completedAppointment ? AppointmentType.FOLLOW_UP : focusedAppointmentType

  if (isPreventDoubleBookingHearingAidEvaluationsFlagEnabled) {
    const isHearingAidEvaluationEnabled = !shouldPreventDoubleBookingHearingAidEvaluations || isRescheduling || isReferredByFittingFlow
    const hasCompletedHearingAidEvaluation = Boolean(completedAppointment)
    const hasConfirmedHearingAidEvaluation = Boolean(confirmedAppointment)

    return {
      hasCompletedHearingAidEvaluation,
      hasConfirmedHearingAidEvaluation,
      isHearingAidEvaluationEnabled,
      isLoading: areAppointmentsLoading,
      nextAppointmentType,
      previouslyCompletedAppointment: completedAppointment,
      previouslyConfirmedAppointment: confirmedAppointment,
    }
  }

  return {
    hasCompletedHearingAidEvaluation: false,
    hasConfirmedHearingAidEvaluation: false,
    isHearingAidEvaluationEnabled: true,
    isLoading: false,
    nextAppointmentType,
  }
}

export { useDoubleBookingContext }
